import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { X, Save, Loader2, Lock, Settings, Tag, Image as ImageIcon, Trash2, ExternalLink, ScrollText } from 'lucide-react';
import toast from 'react-hot-toast';
import { learningPathApiService, LearningPathDto } from '@/services/api/learning-path-api.service';
import { useForm } from 'react-hook-form';
import CertificateTemplateSelect from '@/components/certificate-builder/CertificateTemplateSelect';
import { CertificateTemplateType } from '@/types/certificate-template.types';
import { TagsField, FileUploadField } from '@/components/common/form-fields';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { cleanText, isCleanText } from '@/utils/sanitize';

const CLEAN_TEXT_ERROR = 'Please use only letters, numbers and standard punctuation';

interface BasicInfoFormData {
  title: string;
  description: string;
  target_role_or_skill: string;
  targeted_audience: string;
}

// A disabled fieldset stops form controls but not drop targets, and FileUploadField
// accepts drag-and-drop on a plain div. Swallowing these in the capture phase keeps
// a published learning path from taking an upload.
const swallowDragEvent = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

interface EditLearningPathModalProps {
  learningPath: LearningPathDto;
  onClose: () => void;
  onSuccess: () => void;
  /** Requires the admin_learning_path_delete permission. Defaults to false so the
   *  delete action stays closed unless a caller explicitly grants it. */
  canDelete?: boolean;
  /** Published learning paths are frozen: settings stay visible but read-only.
   *  Deleting is unaffected — that is governed by canDelete alone. */
  isPublished?: boolean;
}

export default function EditLearningPathModal({
  learningPath,
  onClose,
  onSuccess,
  canDelete = false,
  isPublished = false
}: EditLearningPathModalProps) {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string>('basic');
  const [confirmDeleteLPChecked, setConfirmDeleteLPChecked] = useState(false);
  const [activeLPUsers, setActiveLPUsers] = useState<any[]>([]);
  const [revokingUserId, setRevokingUserId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formData, setFormData] = useState({
    title: learningPath.title || '',
    description: learningPath.description || '',
    target_role_or_skill: learningPath.target_role_or_skill || '',
    targeted_audience: learningPath.targeted_audience || '',
    difficulty_progression: learningPath.difficulty_progression || 'mixed',
    tags: learningPath.tags || [],
    outcomes: learningPath.outcomes || [],
    suggestions: learningPath.suggestions || [],
    banner_image_url: learningPath.banner_image_url || '',
    thumbnail_image_url: learningPath.thumbnail_image_url || '',
  });

  // Basic info text fields use the shared Input/Textarea (clean-text check + RTL), which need react-hook-form
  const { control: basicInfoControl, getValues: getBasicInfo, setError: setBasicInfoError } = useForm<BasicInfoFormData>({
    defaultValues: {
      title: formData.title,
      description: formData.description,
      target_role_or_skill: formData.target_role_or_skill,
      targeted_audience: formData.targeted_audience,
    },
  });
  // '' = default template; sent only when changed (an unchanged but since-deleted id would fail the save)
  const savedCertificateTemplateId = learningPath.certificate_template_id || '';
  const [certificateTemplateId, setCertificateTemplateId] = useState(savedCertificateTemplateId);

  const sections = [
    { id: 'basic', label: 'Basic Info', icon: Settings },
    { id: 'tags', label: 'Tags', icon: Tag },
    { id: 'assets', label: 'Brand Assets', icon: ImageIcon },
    { id: 'certificate', label: 'Certificate', icon: ScrollText },
  ];

  const updateMutation = useMutation({
    mutationFn: (data: typeof formData & { certificate_template_id?: string }) =>
      learningPathApiService.updateLearningPath(learningPath._id, data),
    onSuccess: () => {
      toast.success('Learning path settings updated successfully!');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update learning path settings');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Checked here too: the fields may be unmounted while another section is open
    const basicInfo = getBasicInfo();
    const requiredFields: Array<[keyof BasicInfoFormData, string]> = [
      ['title', 'Title is required'],
      ['description', 'Description is required'],
      ['target_role_or_skill', 'Target role/skill is required'],
    ];
    const missing = requiredFields.find(([field]) => !basicInfo[field]?.trim());
    if (missing) {
      setBasicInfoError(missing[0], { message: missing[1] });
      setActiveSection('basic');
      toast.error(missing[1]);
      return;
    }

    // Allowlist-validate free-text (VAPT #12/#13)
    const invalidFields = (Object.keys(basicInfo) as Array<keyof BasicInfoFormData>).filter(field => !isCleanText(basicInfo[field]));
    if (invalidFields.length > 0) {
      invalidFields.forEach(field => setBasicInfoError(field, { message: CLEAN_TEXT_ERROR }));
      setActiveSection('basic');
      toast.error(CLEAN_TEXT_ERROR);
      return;
    }

    // Sanitize free-text before save
    updateMutation.mutate({
      ...formData,
      title: cleanText(basicInfo.title),
      description: cleanText(basicInfo.description),
      target_role_or_skill: cleanText(basicInfo.target_role_or_skill),
      targeted_audience: cleanText(basicInfo.targeted_audience),
      tags: (formData.tags || [])
        .map((t: string) => cleanText(t))
        .filter((t: string) => t.length > 0 && isCleanText(t) && /[\p{L}\p{N}]/u.test(t)),
      ...(certificateTemplateId !== savedCertificateTemplateId ? { certificate_template_id: certificateTemplateId } : {}),
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4 animate-in fade-in duration-200">
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-white rounded-t-xl border-b px-6 py-5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-xl">
              <Settings className="w-6 h-6 text-gray-700" />
            </div>
            <div className="text-left">
              <h2 className="text-xl font-bold text-gray-900">Edit Learning Path Settings</h2>
              <p className="text-gray-500 text-xs mt-0.5">Configure learning path metadata, tags, and assets</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Main Content Area with Sidebar */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Sidebar Navigation */}
          <div className="md:w-64 border-b md:border-b-0 md:border-r bg-gray-50 overflow-x-auto md:overflow-y-auto flex flex-col p-4 flex-shrink-0">
            <nav className="flex md:flex-col gap-1.5 h-full justify-between">
              <div className="flex md:flex-col gap-1.5">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-200 ${isActive
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span>{section.label}</span>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                disabled={!canDelete}
                title={!canDelete ? 'You do not have permission to delete learning paths' : undefined}
                onClick={() => {
                  setActiveSection('delete');
                  setConfirmDeleteLPChecked(false);
                  setActiveLPUsers([]);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${activeSection === 'delete'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <Trash2 className="w-4 h-4 flex-shrink-0" />
                <span>Delete Learning Path</span>
              </button>
            </nav>
          </div>

          {/* Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden bg-white">
            <div className="flex-1 overflow-y-auto p-6">
              {/* Editable settings. A published learning path is frozen, so these
                  stay visible but disabled — the delete section below is
                  deliberately outside this fieldset. */}
              <fieldset
                disabled={isPublished}
                onDragOverCapture={isPublished ? swallowDragEvent : undefined}
                onDropCapture={isPublished ? swallowDragEvent : undefined}
                className={isPublished ? 'opacity-60 cursor-not-allowed [&_*]:cursor-not-allowed' : undefined}
              >
              {isPublished && activeSection !== 'delete' && (
                <div className="mb-4 flex items-start gap-2.5 px-3 py-2.5 bg-green-50 border border-green-200 rounded-lg text-left">
                  <Lock className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-900">
                    This learning path is published, so its settings are read-only.
                  </p>
                </div>
              )}
              {/* === BASIC INFORMATION === */}
              {activeSection === 'basic' && (
                <section className="space-y-5 animate-in fade-in duration-200 text-left">
                  <Input<BasicInfoFormData>
                    name="title"
                    control={basicInfoControl}
                    label="Title"
                    required
                    type="text"
                    rules={{ validate: { required: (v: string) => !!v?.trim() || 'Title is required' } }}
                    placeholder="e.g., Full Stack Web Development Path"
                  />

                  <Textarea<BasicInfoFormData>
                    name="description"
                    control={basicInfoControl}
                    label="Description"
                    required
                    rows={4}
                    rules={{ validate: { required: (v: string) => !!v?.trim() || 'Description is required' } }}
                    placeholder="Describe what learners will achieve..."
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input<BasicInfoFormData>
                      name="target_role_or_skill"
                      control={basicInfoControl}
                      label="Target Role/Skill"
                      required
                      type="text"
                      rules={{ validate: { required: (v: string) => !!v?.trim() || 'Target role/skill is required' } }}
                      placeholder="e.g., Full Stack Developer"
                    />

                    <Input<BasicInfoFormData>
                      name="targeted_audience"
                      control={basicInfoControl}
                      label="Target Audience"
                      type="text"
                      placeholder="e.g., Beginners, Professionals"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Difficulty Progression
                    </label>
                    <select
                      value={formData.difficulty_progression}
                      onChange={(e) => setFormData({ ...formData, difficulty_progression: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                      <option value="mixed">Mixed (Beginner to Advanced)</option>
                    </select>
                  </div>
                </section>
              )}

              {/* === TAGS === */}
              {activeSection === 'tags' && (
                <section className="animate-in fade-in duration-200 text-left">
                  <TagsField
                    label="Tags"
                    description="Add relevant tags to help categorize this learning path"
                    value={formData.tags}
                    onChange={(tags) => setFormData({ ...formData, tags })}
                    placeholder="Add a tag (e.g., JavaScript, Beginner, Web Development)"
                    maxTags={10}
                  />
                </section>
              )}

              {/* === BRAND ASSETS === */}
              {activeSection === 'assets' && (
                <section className="space-y-5 animate-in fade-in duration-200 text-left">
                  <div>
                    <h4 className="text-base font-semibold text-gray-900 mb-1">Brand Assets</h4>
                    <p className="text-xs text-gray-500">Upload banner and thumbnail images for this learning path</p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <FileUploadField
                      label="Banner Image"
                      description="Displayed as the header image (Recommended: 1200x400px)"
                      currentFileUrl={formData.banner_image_url}
                      onFileChange={(url) => setFormData({ ...formData, banner_image_url: url || '' })}
                      accept="image/*"
                      fileType="image"
                      maxSizeMB={10}
                      metadata={{ category: 'learning-path', type: 'banner' }}
                    />

                    <FileUploadField
                      label="Thumbnail Image"
                      description="Displayed in lists (Recommended: 400x400px)"
                      currentFileUrl={formData.thumbnail_image_url}
                      onFileChange={(url) => setFormData({ ...formData, thumbnail_image_url: url || '' })}
                      accept="image/*"
                      fileType="image"
                      maxSizeMB={10}
                      metadata={{ category: 'learning-path', type: 'thumbnail' }}
                    />
                  </div>
                </section>
              )}

              {/* === CERTIFICATE TEMPLATE === */}
              {activeSection === 'certificate' && (
                <section className="space-y-5 animate-in fade-in duration-200 text-left">
                  <div>
                    <h4 className="text-base font-semibold text-gray-900 mb-1">Certificate Template</h4>
                    <p className="text-xs text-gray-500">The certificate design learners get when they complete this learning path.</p>
                  </div>
                  <div className="max-w-xl">
                    <CertificateTemplateSelect
                      templateType={CertificateTemplateType.LEARNING_PATH_COMPLETION}
                      value={certificateTemplateId}
                      onChange={setCertificateTemplateId}
                      disabled={isPublished}
                    />
                    <p className="text-xs text-gray-400 mt-2">Applies to certificates issued after saving.</p>
                  </div>
                </section>
              )}
              </fieldset>

              {/* === DELETE LEARNING PATH === */}
              {activeSection === 'delete' && (
                <section className="space-y-6 max-w-2xl mx-auto py-2 animate-in fade-in duration-200">
                  <div className="border border-red-100 rounded-xl p-5 bg-red-50/5">
                    <div className="max-w-xl mx-auto text-start">
                      <div className="flex items-start gap-5 mb-6">
                        {/* Icon */}
                        <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                          <Trash2 className="w-7 h-7 text-red-600" />
                        </div>

                        {/* Content */}
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Delete Learning Path
                          </h3>
                          <p className="text-sm text-gray-600 mt-0.5 leading-relaxed">
                            This action is permanent and cannot be undone. All user enrollments and progress metrics linked to this learning path will also be deleted.
                          </p>
                        </div>
                      </div>

                      {/* Confirmation Checkbox */}
                      <div className="flex items-start gap-3 text-left">
                        <input
                          type="checkbox"
                          id="confirm-delete-lp-checkbox-modal"
                          checked={confirmDeleteLPChecked}
                          onChange={(e) => setConfirmDeleteLPChecked(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                        />
                        <label
                          htmlFor="confirm-delete-lp-checkbox-modal"
                          className="text-xs text-gray-700 select-none cursor-pointer font-medium leading-relaxed"
                        >
                          I confirm that I want to delete this learning path. I understand this action is permanent.
                        </label>
                      </div>

                      {/* Active Learners Blockers List */}
                      {activeLPUsers.length > 0 && (
                        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-start">
                          <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-2">
                            Active Learners ({activeLPUsers.length})
                          </h4>
                          <p className="text-xs text-amber-700 mb-3">
                            This learning path cannot be deleted because it is currently assigned to the active learners listed below. Please revoke their assignments first.
                          </p>
                          <div className="max-h-40 overflow-y-auto space-y-2 pr-1">
                            {activeLPUsers.map((user) => (
                              <div key={user._id} className="flex items-center justify-between p-2 bg-white rounded border border-amber-100 text-xs shadow-sm">
                                <div>
                                  <div className="font-semibold text-gray-900">
                                    {user.given_name} {user.family_name || ''}
                                  </div>
                                  <div className="text-gray-500 text-[10px]">{user.email}</div>
                                </div>
                                <button
                                  type="button"
                                  disabled={revokingUserId !== null}
                                  onClick={() => {
                                    setRevokingUserId(user._id);
                                    navigate(`/admin/learning-paths/${learningPath._id}/users/${user._id}/progress`);
                                  }}
                                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all disabled:opacity-50"
                                  title="Revoke user assignment"
                                >
                                  {revokingUserId === user?._id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <ExternalLink className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Buttons */}
                      <div className="flex justify-center gap-3 pt-6 mt-6 border-t border-gray-100">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveSection('basic');
                            setActiveLPUsers([]);
                          }}
                          className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Keep Learning Path
                        </button>

                        <button
                          type="button"
                          disabled={!canDelete || !confirmDeleteLPChecked || isDeleting}
                          title={!canDelete ? 'You do not have permission to delete learning paths' : undefined}
                          onClick={async () => {
                            if (!canDelete) return;
                            setIsDeleting(true);
                            setActiveLPUsers([]);
                            try {
                              const res = await learningPathApiService.deleteLearningPath(learningPath._id);
                              const users = res.meta_data?.active_users;
                              if (Array.isArray(users) && users.length > 0) {
                                setActiveLPUsers(users);
                              } else {
                                toast.success('Learning path deleted successfully!');
                                onClose();
                                navigate('/admin/learning-paths');
                              }
                            } catch (err: any) {
                              console.error('Delete learning path failed:', err);
                              toast.error(err?.response?.data?.message || 'Failed to delete learning path');
                            } finally {
                              setIsDeleting(false);
                            }
                          }}
                          className="px-4 py-2 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-sm transition-colors"
                        >
                          {isDeleting ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-3.5 h-3.5" />
                              Delete Learning Path Permanently
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </div>

            {/* Footer */}
            {activeSection !== 'delete' && (
              <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 bg-gray-50 flex-shrink-0 rounded-b-xl">
                <p className="text-xs text-gray-500 hidden sm:block">
                  {isPublished
                    ? 'Published learning paths are read-only'
                    : 'Make sure to save your changes before closing'}
                </p>
                <div className="flex items-center gap-3 ml-auto">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 sm:flex-none px-4 sm:px-5 py-2 sm:py-2.5 text-sm sm:text-base text-gray-700 bg-white-100 border border-gray-300 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {isPublished ? 'Close' : 'Cancel'}
                  </button>
                  {!isPublished && (
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {updateMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
